import React, { useEffect, useState } from "react";
import { api, resolveUrl, type Tenancy, type LedgerEntry, type MaintenanceRequest, type TenancyExit } from "../../api";
import { useToast } from "../../ToastContext";
import { RentalAgreement } from "../shared/RentalAgreement";
import { RentReceipt } from "../shared/RentReceipt";
import { HomePromoCarousel } from "../shared/HomePromoCarousel";
import { MoveInDetails } from "./MoveInDetails";
import { useLanguage } from "../../i18n";
import { initials } from "../../format";

const MAINTENANCE_ICON = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="none">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

const UPI_ICON = (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="6" width="20" height="14" rx="2.5" />
    <path d="M2 10h20M6 15h4" />
  </svg>
);

const COPY_ICON = (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const REQUEST_STATUS_KEY: Record<string, string> = {
  open: "common.statusOpen",
  in_progress: "common.statusInProgress",
  resolved: "common.statusResolved",
};

function fmtINR(n: number) {
  return "₹" + Number(n).toLocaleString("en-IN");
}

function monthLabel(month: string) {
  const [y, m] = month.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("en-IN", { month: "long", year: "numeric" });
}

// Clamped to the month's real last day so a due day of 31 doesn't overflow
// into the next month for e.g. February.
function daysUntilRentDue(month: string, rentDueDay: number): number {
  const [y, m] = month.split("-").map(Number);
  const daysInMonth = new Date(y, m, 0).getDate();
  const dueDate = new Date(y, m - 1, Math.min(rentDueDay, daysInMonth));
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  dueDate.setHours(0, 0, 0, 0);
  return Math.round((dueDate.getTime() - today.getTime()) / 86_400_000);
}

function roomTag(property: Tenancy["property"], tenancyId: string): string | null {
  if (!property) return null;
  for (const room of property.rooms ?? []) {
    for (const bed of room.beds) {
      if (bed.tenancy?.id === tenancyId) return `${room.sharingType} · ${bed.label}`;
    }
  }
  return property.type ?? null;
}

export function TenantHome({ onNavigate }: { onNavigate: (v: "browse" | "payments" | "requests" | "refer") => void }) {
  const { t } = useLanguage();
  const [tenancies, setTenancies] = useState<Tenancy[] | null>(null);
  const [entries, setEntries] = useState<LedgerEntry[]>([]);
  const [requests, setRequests] = useState<MaintenanceRequest[]>([]);
  const [exits, setExits] = useState<TenancyExit[]>([]);
  const [busy, setBusy] = useState(false);
  const [showAgreement, setShowAgreement] = useState(false);
  const [showMoveIn, setShowMoveIn] = useState(false);
  const [viewingReceipt, setViewingReceipt] = useState<any | null>(null);
  const toast = useToast();

  async function load() {
    const [{ tenancies }, { ledgers }, { requests }, { exits }] = await Promise.all([
      api.get("/api/tenancies/mine"),
      api.get("/api/ledger/mine"),
      api.get("/api/requests/mine"),
      api.get("/api/tenancies/exits/mine"),
    ]);
    setTenancies(tenancies);
    setEntries(ledgers[0]?.entries ?? []);
    setRequests(requests);
    setExits(exits);
  }

  useEffect(() => {
    load();
  }, []);

  if (!tenancies) return <div className="spinner" />;

  if (tenancies.length === 0) {
    const lastExit = exits[0];
    return (
      <>
        <HomePromoCarousel onNavigate={onNavigate} />
        {lastExit && (
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="sub" style={{ fontWeight: 800, marginBottom: 8 }}>{t("tenantHome.moveOutSettlement")}</div>
            <div className="card-row">
              <span className="sub">{lastExit.property?.label ?? t("tenantHome.yourLastPlace")}</span>
              <span className="badge paid">
                {t("tenantHome.exitedOn", {
                  date: new Date(lastExit.exitDate).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }),
                })}
              </span>
            </div>
            <div className="card-row" style={{ marginTop: 8 }}>
              <span className="sub">{t("tenantHome.depositPaid")}</span>
              <b>{fmtINR(lastExit.depositAmount)}</b>
            </div>
            {lastExit.deductions > 0 && (
              <div className="card-row" style={{ marginTop: 8 }}>
                <span className="sub">{t("tenantHome.deductionsLabel")}{lastExit.deductionNote ? ` — ${lastExit.deductionNote}` : ""}</span>
                <b>-{fmtINR(lastExit.deductions)}</b>
              </div>
            )}
            <div className="card-row" style={{ marginTop: 8, paddingTop: 8, borderTop: "1px dashed var(--line)" }}>
              <span className="sub">{t("tenantHome.refundableDeposit")}</span>
              <b style={{ fontSize: 16 }}>{fmtINR(lastExit.refundAmount)}</b>
            </div>
          </div>
        )}
        {lastExit ? (
          <div className="empty-state">
            {t("tenantHome.noActiveTenancyLine1")}
            <br />
            {t("tenantHome.noActiveTenancyLine2")}
          </div>
        ) : (
          <div className="empty-state" style={{ padding: "28px 12px 20px" }}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>🏠</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: "var(--ink)", marginBottom: 6 }}>
              {t("tenantHome.welcomeTitle")}
            </div>
            <div style={{ fontSize: 13, lineHeight: 1.5 }}>{t("tenantHome.welcomeSub")}</div>
          </div>
        )}
        <div className="empty-state" style={{ marginTop: 4, marginBottom: 16 }}>
          {t("tenantBrowse.moveInHint")}
        </div>
        <button className="btn-solid" onClick={() => onNavigate("browse")}>
          {t("tenantHome.browsePropertiesBtn")}
        </button>
      </>
    );
  }

  const tenancy = tenancies[0];

  if (showAgreement) {
    return (
      <RentalAgreement
        tenancyId={tenancy.id}
        tenantName={tenancy.name}
        tenantPhone={tenancy.phone}
        ownerName={tenancy.property?.ownerName}
        ownerPhone={tenancy.property?.ownerPhone}
        propertyLabel={tenancy.property?.label ?? "—"}
        propertyLocality={tenancy.property?.locality}
        propertyType={tenancy.property?.type}
        rent={tenancy.rent}
        deposit={tenancy.deposit}
        startDate={tenancy.startDate}
        onBack={() => setShowAgreement(false)}
      />
    );
  }

  if (showMoveIn) {
    return <MoveInDetails tenancy={tenancy} onBack={() => setShowMoveIn(false)} />;
  }

  if (viewingReceipt) {
    return (
      <RentReceipt
        entry={viewingReceipt.entry}
        tenantName={tenancy.name}
        propertyLabel={tenancy.property?.label ?? "—"}
        propertyLocality={tenancy.property?.locality}
        ownerName={tenancy.property?.ownerName}
        justPaid={viewingReceipt.justPaid}
        onBack={() => setViewingReceipt(null)}
      />
    );
  }

  const current = entries[0];
  const overdue = entries.slice(1).some((e) => e.status === "pending");
  const openRequests = requests.filter((r) => r.status !== "resolved").slice(0, 2);
  const tag = roomTag(tenancy.property, tenancy.id);

  async function copyUpiId(e: React.MouseEvent, upiId: string) {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(upiId);
      toast(t("tenantHome.upiCopiedToast"));
    } catch {
      toast(t("tenantHome.upiCopyFailedToast"));
    }
  }

  // Paying needs the UPI/bank/cash options and the reference field, which live
  // on the Payments tab, so the Home button hands over to it.
  function payNow() {
    try {
      sessionStorage.setItem("toletbook_open_pay", "1");
    } catch {}
    onNavigate("payments");
  }

  return (
    <>
      {current && (
        <div className={`rent-card${current.status === "pending" && overdue ? " is-overdue" : ""}`}>
          <div className="rent-card-top">
            <span>{monthLabel(current.month)}</span>
            {current.status === "paid" ? (
              <span className="rent-status-pill"><span className="dot" />{t("tenantHome.paidStatus")}</span>
            ) : current.status === "pending_confirmation" ? (
              <span className="rent-status-pill pending-confirmation"><span className="dot" />{t("tenantHome.awaitingConfirmationStatus")}</span>
            ) : (
              <span className={`rent-status-pill${overdue ? " overdue" : ""}`}>
                <span className="dot" />{overdue ? t("tenantHome.overdueStatus") : t("tenantHome.dueStatus")}
              </span>
            )}
          </div>
          <div className="rent-amount">{fmtINR(current.amount)}</div>
          <p className="rent-sub">{t("tenantHome.rentForMonth", { month: monthLabel(current.month) })}</p>
          {current.status === "pending" && (() => {
            const days = daysUntilRentDue(current.month, tenancy.rentDueDay);
            return (
              <p className="rent-sub" style={{ marginTop: -10 }}>
                {days > 0
                  ? t("tenantHome.dueInDays", { count: days })
                  : days === 0
                  ? t("tenantHome.dueTodayLabel")
                  : t("tenantHome.overdueByDays", { count: -days })}
              </p>
            );
          })()}
          {current.status === "pending" && current.reminderSentAt && (
            <div
              style={{
                background: "var(--cash-tint)",
                color: "var(--cash)",
                fontSize: 12,
                fontWeight: 700,
                padding: "8px 12px",
                borderRadius: 11,
                marginBottom: 12,
              }}
            >
              {t("tenantHome.reminderBanner")}
            </div>
          )}
          {current.status === "paid" ? (
            <div className="rent-card-actions">
              <div className="rent-paid-note" style={{ flex: 1 }}>
                {t("tenantHome.paidCheck")}
                {current.paidOn ? ` ${t("tenantHome.onDate", { date: new Date(current.paidOn).toLocaleDateString("en-IN") })}` : ""}
                {current.mode ? ` ${t("tenantHome.viaMode", { mode: current.mode.toUpperCase() })}` : ""}
              </div>
              <button
                className="btn-outline btn-sm"
                onClick={() => setViewingReceipt({ entry: current, justPaid: false })}
              >
                {t("tenantHome.receiptBtn")}
              </button>
            </div>
          ) : current.status === "pending_confirmation" ? (
            <div className="rent-card-actions">
              <div className="rent-paid-note" style={{ flex: 1 }}>
                {t("tenantHome.pendingConfirmationNote", { mode: current.mode?.toUpperCase() ?? "UPI" })}
              </div>
            </div>
          ) : (
            <div className="rent-card-actions">
              <button className="btn-pay" disabled={busy} onClick={payNow}>
                {busy ? t("tenantHome.payingBtn") : t("tenantHome.payAmount", { amount: fmtINR(current.amount) })}
              </button>
            </div>
          )}
        </div>
      )}

      <HomePromoCarousel onNavigate={onNavigate} />

      <div className="prop-card" style={{ cursor: "pointer", marginBottom: 14 }} onClick={() => setShowMoveIn(true)}>
        <div className="prop-top">
          <div className="prop-top-main">
            <div className="prop-thumb">
              {tenancy.property?.photos?.[0] ? (
                <img src={resolveUrl(tenancy.property.photos[0])} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              ) : (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />
                  <path d="M9 22V12h6v10" />
                </svg>
              )}
            </div>
            <div>
              <div className="prop-addr">{tenancy.property?.label}</div>
              <div className="prop-locality">{tenancy.property?.locality}</div>
            </div>
          </div>
        </div>
        <div className="prop-body">
          {tag && <span className="room-tag">{tag}</span>}
          {tenancy.property?.ownerName && (
            <div
              className="landlord-row"
              style={tag ? { marginTop: 10, paddingTop: 10, borderTop: "1px dashed var(--line)" } : undefined}
            >
              <div className="landlord-who">
                <div className="avatar-sm">{initials(tenancy.property.ownerName)}</div>
                <div className="landlord-name">
                  {t("tenantHome.ownerLabel")}
                  <b>{tenancy.property.ownerName}</b>
                </div>
              </div>
              {tenancy.property.ownerPhone && (
                <a
                  className="t-icon-btn"
                  href={`tel:${tenancy.property.ownerPhone}`}
                  aria-label={t("tenantHome.callOwnerAria")}
                  onClick={(e) => e.stopPropagation()}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                  </svg>
                </a>
              )}
            </div>
          )}
          {tenancy.property?.ownerUpiId && (
            <div className="landlord-row" style={{ marginTop: 10, paddingTop: 10, borderTop: "1px dashed var(--line)" }}>
              <div className="landlord-who">
                <div className="avatar-sm" style={{ background: "var(--upi-tint)", color: "var(--upi)" }}>{UPI_ICON}</div>
                <div className="landlord-name">
                  {t("tenantHome.upiIdLabel")}
                  <b style={{ fontFamily: "monospace" }}>{tenancy.property.ownerUpiId}</b>
                </div>
              </div>
              <button
                type="button"
                className="t-icon-btn"
                aria-label={t("tenantHome.copyUpiAria")}
                onClick={(e) => copyUpiId(e, tenancy.property!.ownerUpiId!)}
              >
                {COPY_ICON}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="qa-grid">
        <button className="qa-item" onClick={() => onNavigate("requests")}>
          <span className="qa-icon">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" /></svg>
          </span>
          <span>{t("tenantHome.raiseRequest")}</span>
        </button>
        <button className="qa-item" onClick={() => setShowAgreement(true)}>
          <span className="qa-icon">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6M9 15h6M9 11h2" /></svg>
          </span>
          <span>{t("tenantHome.viewLease")}</span>
        </button>
        <button className="qa-item" onClick={() => onNavigate("payments")}>
          <span className="qa-icon">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="6" width="20" height="14" rx="2.5" /><path d="M2 10h20M6 15h4" /></svg>
          </span>
          <span>{t("tenantHome.receiptsNav")}</span>
        </button>
        <button className="qa-item" onClick={() => onNavigate("browse")}>
          <span className="qa-icon">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></svg>
          </span>
          <span>{t("tenantHome.browseRooms")}</span>
        </button>
      </div>

      <div className="sec-head">
        <h2>{t("tenantHome.openRequestsHeading")}</h2>
        <button className="sec-link" onClick={() => onNavigate("requests")}>{t("tenantHome.viewAll")}</button>
      </div>
      {openRequests.length === 0 ? (
        <div className="empty-state">{t("tenantHome.noOpenRequests")}</div>
      ) : (
        openRequests.map((r) => (
          <div className="req-card maintenance" key={r.id}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
              <div className="req-icon maintenance">{MAINTENANCE_ICON}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <b style={{ fontSize: 13.5 }}>{r.title}</b>
                <div className="sub">{r.description || t("tenantHome.noDetailsProvided")}</div>
              </div>
              <span className={`badge ${r.status}`}>{t(REQUEST_STATUS_KEY[r.status] ?? r.status)}</span>
            </div>
          </div>
        ))
      )}
    </>
  );
}
